import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { PlusCircle, Trash, FilePdf, UploadSimple, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";

const uid = (p) => `${p}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/**
 * Competitor Benchmarking editor.
 * @param {string} productId
 * @param {{rows:Array,competitors:Array,winners:Object}} value
 * @param {(next)=>void} onChange
 */
export default function BenchmarkEditor({ productId, value, onChange }) {
  const rows = value?.rows || [];
  const competitors = value?.competitors || [];
  const winners = value?.winners || {};
  const [uploading, setUploading] = useState(null);
  const fileRefs = useRef({});

  const update = (patch) => onChange({ rows, competitors, winners, ...patch });

  // ---- competitors ----
  const addCompetitor = () => {
    if (competitors.length >= 5) return toast.error("Up to 5 competitors");
    update({ competitors: [...competitors, { competitor_id: uid("bmc_"), name: "", product_name: "", values: {}, datasheet_path: null, datasheet_name: null }] });
  };
  const setCompetitor = (id, key, val) =>
    update({ competitors: competitors.map((c) => (c.competitor_id === id ? { ...c, [key]: val } : c)) });
  const removeCompetitor = (id) =>
    update({
      competitors: competitors.filter((c) => c.competitor_id !== id),
      winners: Object.fromEntries(Object.entries(winners).map(([k, v]) => [k, v === id ? "" : v])),
    });

  const uploadDatasheet = async (compId, file) => {
    if (!file) return;
    if (!productId) return toast.error("Save the product first, then upload data sheets");
    setUploading(compId);
    try {
      const fd = new FormData();
      fd.append("pdf", file);
      const res = await api.post(`/products/${productId}/benchmark/datasheet`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCompetitor(compId, "datasheet_path", res.data.path);
      update({
        competitors: competitors.map((c) =>
          c.competitor_id === compId ? { ...c, datasheet_path: res.data.path, datasheet_name: res.data.datasheet_name } : c
        ),
      });
      toast.success("Data sheet uploaded — remember to Save");
    } catch (e) {
      toast.error("Upload failed");
    }
    setUploading(null);
  };

  // ---- rows ----
  const addRow = () => update({ rows: [...rows, { row_id: uid("row_"), label: "", ours: "" }] });
  const setRow = (id, key, val) => update({ rows: rows.map((r) => (r.row_id === id ? { ...r, [key]: val } : r)) });
  const removeRow = (id) =>
    update({
      rows: rows.filter((r) => r.row_id !== id),
      winners: Object.fromEntries(Object.entries(winners).filter(([k]) => k !== id)),
      competitors: competitors.map((c) => {
        const v = { ...(c.values || {}) };
        delete v[id];
        return { ...c, values: v };
      }),
    });
  const setCell = (compId, rowId, val) =>
    update({
      competitors: competitors.map((c) =>
        c.competitor_id === compId ? { ...c, values: { ...(c.values || {}), [rowId]: val } } : c
      ),
    });
  const setWinner = (rowId, val) => update({ winners: { ...winners, [rowId]: val } });

  return (
    <div className="pt-4 border-t border-zinc-200 space-y-4" data-testid="benchmark-editor">
      <div>
        <div className="mt-tiny text-[#FF5A1F]">Competitor Benchmarking</div>
        <p className="text-xs text-zinc-500 mt-0.5">
          Compare this product spec-by-spec against direct competitors. Mark who wins each row; the
          client sees a highlighted comparison table.
        </p>
      </div>

      {/* Competitors */}
      <div className="space-y-2">
        <div className="text-xs font-semibold text-zinc-700">Competitors ({competitors.length}/5)</div>
        {competitors.map((c) => (
          <div key={c.competitor_id} className="bg-zinc-50 border border-zinc-200 p-3 space-y-2" data-testid={`benchmark-competitor-${c.competitor_id}`}>
            <div className="flex items-center gap-2">
              <Input
                value={c.name}
                onChange={(e) => setCompetitor(c.competitor_id, "name", e.target.value)}
                placeholder="Competitor name (e.g. Häfele)"
                data-testid={`benchmark-competitor-name-${c.competitor_id}`}
                className="rounded-none h-10 border-zinc-200 flex-1"
              />
              <button
                onClick={() => removeCompetitor(c.competitor_id)}
                data-testid={`benchmark-competitor-remove-${c.competitor_id}`}
                className="text-red-500 hover:bg-red-50 p-2"
              >
                <Trash size={16} />
              </button>
            </div>
            <Input
              value={c.product_name}
              onChange={(e) => setCompetitor(c.competitor_id, "product_name", e.target.value)}
              placeholder="Their product name (e.g. Metalla 510)"
              data-testid={`benchmark-competitor-product-${c.competitor_id}`}
              className="rounded-none h-10 border-zinc-200"
            />
            <input
              ref={(el) => (fileRefs.current[c.competitor_id] = el)}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => uploadDatasheet(c.competitor_id, e.target.files?.[0])}
            />
            <button
              onClick={() => fileRefs.current[c.competitor_id]?.click()}
              disabled={uploading === c.competitor_id}
              data-testid={`benchmark-datasheet-upload-${c.competitor_id}`}
              className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-[#0047AB] hover:text-[#0047AB] px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50"
            >
              {c.datasheet_path ? <FilePdf size={14} weight="fill" /> : <UploadSimple size={14} />}
              {uploading === c.competitor_id
                ? "Uploading..."
                : c.datasheet_path
                ? c.datasheet_name || "Data sheet attached"
                : "Upload comparison data sheet (PDF)"}
            </button>
          </div>
        ))}
        <button
          onClick={addCompetitor}
          data-testid="benchmark-add-competitor"
          className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-[#0047AB] hover:text-[#0047AB] px-3 py-1.5 text-xs font-medium transition-colors"
        >
          <PlusCircle size={14} /> Add competitor
        </button>
      </div>

      {/* Spec rows */}
      {competitors.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-zinc-700">Spec rows</div>
          <div className="overflow-x-auto border border-zinc-200">
            <table className="w-full border-collapse text-sm" data-testid="benchmark-rows-table">
              <thead>
                <tr className="bg-zinc-50 text-left">
                  <th className="p-2 mt-tiny text-zinc-500 min-w-[150px]">Spec</th>
                  <th className="p-2 mt-tiny text-[#0047AB] min-w-[110px]">STON LINK</th>
                  {competitors.map((c) => (
                    <th key={c.competitor_id} className="p-2 mt-tiny text-zinc-500 min-w-[110px]">
                      {c.name || "Competitor"}
                    </th>
                  ))}
                  <th className="p-2 mt-tiny text-zinc-500 min-w-[130px]">Winner</th>
                  <th className="p-2 w-8" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.row_id} className="border-t border-zinc-100" data-testid={`benchmark-row-${r.row_id}`}>
                    <td className="p-1.5">
                      <input
                        value={r.label}
                        onChange={(e) => setRow(r.row_id, "label", e.target.value)}
                        placeholder="Hinge cycles"
                        data-testid={`benchmark-row-label-${r.row_id}`}
                        className="w-full border border-zinc-200 h-9 px-2 outline-none focus:border-[#0047AB]"
                      />
                    </td>
                    <td className="p-1.5">
                      <input
                        value={r.ours}
                        onChange={(e) => setRow(r.row_id, "ours", e.target.value)}
                        placeholder="200,000"
                        data-testid={`benchmark-row-ours-${r.row_id}`}
                        className="w-full border border-zinc-200 h-9 px-2 outline-none focus:border-[#0047AB]"
                      />
                    </td>
                    {competitors.map((c) => (
                      <td key={c.competitor_id} className="p-1.5">
                        <input
                          value={c.values?.[r.row_id] ?? ""}
                          onChange={(e) => setCell(c.competitor_id, r.row_id, e.target.value)}
                          placeholder="100,000"
                          data-testid={`benchmark-cell-${c.competitor_id}-${r.row_id}`}
                          className="w-full border border-zinc-200 h-9 px-2 outline-none focus:border-[#0047AB]"
                        />
                      </td>
                    ))}
                    <td className="p-1.5">
                      <select
                        value={winners[r.row_id] || ""}
                        onChange={(e) => setWinner(r.row_id, e.target.value)}
                        data-testid={`benchmark-winner-${r.row_id}`}
                        className="w-full border border-zinc-200 h-9 px-1 outline-none focus:border-[#0047AB] bg-white"
                      >
                        <option value="">— none —</option>
                        <option value="ours">🏆 STON LINK</option>
                        {competitors.map((c) => (
                          <option key={c.competitor_id} value={c.competitor_id}>
                            {c.name || "Competitor"}
                          </option>
                        ))}
                        <option value="tie">Tie</option>
                      </select>
                    </td>
                    <td className="p-1.5 text-center">
                      <button
                        onClick={() => removeRow(r.row_id)}
                        data-testid={`benchmark-row-remove-${r.row_id}`}
                        className="text-red-500 hover:bg-red-50 p-1.5"
                      >
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={competitors.length + 4} className="p-4 text-center text-zinc-400 text-xs">
                      No spec rows yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <button
            onClick={addRow}
            data-testid="benchmark-add-row"
            className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-[#0047AB] hover:text-[#0047AB] px-3 py-1.5 text-xs font-medium transition-colors"
          >
            <PlusCircle size={14} /> Add spec row
          </button>
          <p className="text-[11px] text-zinc-400 flex items-center gap-1">
            <Trophy size={12} /> The "Winner" cell gets a green highlight + checkmark on the client table.
          </p>
        </div>
      )}
    </div>
  );
}
